import React, { useContext, useEffect, useReducer } from 'react'
import { AddressHistoryContext, AddressDetails, AddressHistoryEntry } from '../AddressHistoryContext'
import { AddressDetailsQuestion } from '../AddressDetails'
import AddressSituation from '../AddressSituation'
import TimeInAddressSituation from '../TimeInAddressSituation'
import AddressHistoryEntryContext, { AddressHistoryEntryState, AddressSubQuestion } from './AddressHistoryEntryContext'
import AddressHistoryEntryReducer from './AddressHistoryEntryReducer'
import { ApplicationFormContext } from '../../../ApplicationForm/ApplicationFormContext'
import { HistoryEntryProps } from '../../HistoryEntry/HistoryEntryProps'
import { fireReactGAEvent } from '../../utils'
import { PrefillReviewPageApplicationFormContext } from '../../../ApplicationForm/PrefillReviewPageApplicationFormContext'
import { useAppFormEventsService } from '../../../../hooks/useAppFormEventsService'

const AddressEntry = ({ existingEntry, disablePrevious = false, entryId, startAtEnd = false, updateFollowingPrefillStep }: HistoryEntryProps<AddressHistoryEntry>): JSX.Element => {
  const { setQuestionName } = useContext(PrefillReviewPageApplicationFormContext)
  const { dispatch: addressHistoryDispatch } = useContext(AddressHistoryContext)
  const { featureSwitches } = useContext(ApplicationFormContext)
  const eventsService = useAppFormEventsService()
  const { reviewPageV3 } = featureSwitches ?? {}

  const addressDetailsComplete = (addressDetails?: AddressDetails) =>
    !!addressDetails && !!(addressDetails.flatNumber || addressDetails.buildingName || addressDetails.buildingNumber) && !!addressDetails.street && !!addressDetails.town && !!addressDetails.postcode

  const calculateStartingQuestion = (): AddressSubQuestion => {
    if (reviewPageV3) return 'ADDRESS_DETAILS'
    if (existingEntry === undefined) return 'ADDRESS_DETAILS'
    if (startAtEnd) return 'TIME_IN_SITUATION'
    if (existingEntry.entrySource === 'FORM' || !addressDetailsComplete(existingEntry.addressDetails)) {
      return 'ADDRESS_DETAILS'
    }
    if (!existingEntry.lengthOfTime) return 'TIME_IN_SITUATION'
    return 'ADDRESS_DETAILS'
  }

  const initialState: AddressHistoryEntryState = {
    entry: existingEntry ?? { entrySource: 'FORM' },
    question: calculateStartingQuestion()
  }

  const [state, dispatch] = useReducer(AddressHistoryEntryReducer(), initialState)

  useEffect(() => {
    // Used for logging purposes in PrefillReviewPageApplicationForm since questionConfig is not available
    if (reviewPageV3) {
      setQuestionName('addressHistory')
    }
  }, [])

  useEffect(() => {
    const label = `qst.addressHistory.${parseInt(entryId, 10) - 1}`
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Question Visible',
      label,
      nonInteraction: true
    })
    eventsService?.sendEvent('AppFormQuestionVisible', { QuestionLabel: label })
  }, [])

  useEffect(() => {
    switch (state.escapeAction) {
      case 'NEXT': {
        addressHistoryDispatch({
          type: 'UPDATE_ENTRY',
          index: entryId,
          value: state.entry
        })
        addressHistoryDispatch({
          type: 'NEXT'
        })
        break
      }
      case 'PREVIOUS': {
        addressHistoryDispatch({
          type: 'UPDATE_ENTRY',
          index: entryId,
          value: state.entry
        })
        addressHistoryDispatch({
          type: 'PREVIOUS'
        })
        break
      }
      default:
        break
    }
  }, [state.escapeAction])

  const buildEntryTitle = (addressDetails?: AddressDetails): string | undefined => {
    if (addressDetails === undefined) return undefined
    const addressTitleParts: string[] = []
    if (addressDetails.flatNumber || addressDetails.buildingName) {
      addressTitleParts.push([addressDetails.flatNumber, addressDetails.buildingName].filter(Boolean).join(' '))
    }
    if (addressDetails.buildingNumber || addressDetails.street) {
      addressTitleParts.push([addressDetails.buildingNumber, addressDetails.street].filter(Boolean).join(' '))
    }
    if (addressDetails.postcode) {
      addressTitleParts.push(addressDetails.postcode)
    }
    return addressTitleParts.join(', ')
  }

  const renderQuestion = (): JSX.Element => {
    switch (state.question) {
      case 'ADDRESS_DETAILS':
        return (
          <AddressDetailsQuestion
            disablePrevious={disablePrevious}
            entryId={entryId}
            existingDetails={state.entry.addressDetails}
            existingDisplayState={state.entry.displayState ? state.entry.displayState : 'SEARCH_RESULTS'}
            existingLoqateResults={state.entry.loqateResults}
            existingPostcodeSearchLabel={state.entry.addressLabel}
            existingPostcodeSearchAttempted={state.entry.postcodeSearchAttempted}
            updateFollowingPrefillStep={updateFollowingPrefillStep}
          />
        )
      case 'STATUS':
        return (
          <AddressSituation
            disablePrevious={disablePrevious}
            entryId={entryId}
            existingSituation={state.entry.addressSituation}
          />
        )
      case 'TIME_IN_SITUATION':
        return (
          <TimeInAddressSituation
            disablePrevious={disablePrevious}
            entryId={entryId}
            existingDuration={state.entry.lengthOfTime}
            entryTitle={buildEntryTitle(state.entry.addressDetails)}
            updateFollowingPrefillStep={updateFollowingPrefillStep}
          />
        )
      default:
        return <></>
    }
  }

  return <AddressHistoryEntryContext.Provider value={{ dispatch }}>{renderQuestion()}</AddressHistoryEntryContext.Provider>
}

export default AddressEntry
